/**
 * Format a phone string as "+<country> <national number>" with dashes.
 *
 * Supports:
 *   - E.164-style input ("+1 4155550101", "+44 20 7946 0958")
 *   - Bare NANP 10-digit input ("4155550101" -> defaults to +1)
 *   - Bare NANP 11-digit starting with 1 ("14155550101")
 *   - Already-formatted values ("+1 415-555-0100") are re-normalized.
 *
 * For unknown / unparseable input the original string is returned so the
 * grid never shows a worse value than what the user typed.
 */
export function formatPhone(input: string | null | undefined): string {
  if (!input) return "";
  const raw = String(input).trim();
  if (!raw || raw === "-") return raw;

  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D+/g, "");
  if (!digits) return raw;

  // Detect country code + national number
  let cc = "";
  let national = digits;

  if (hasPlus) {
    // Common CCs: 1 (NANP), 7 (RU/KZ), 20-998 (variable length 1-3)
    if (digits.startsWith("1") && digits.length === 11) {
      cc = "1";
      national = digits.slice(1);
    } else if (digits.length > 10) {
      // Best effort: assume 1-3 digit CC, leave 7-10 digits national
      const ccLen = digits.length - 10 > 0 ? Math.min(3, digits.length - 7) : 1;
      cc = digits.slice(0, ccLen);
      national = digits.slice(ccLen);
    } else {
      // Short international number, unusual — return as-is
      return raw;
    }
  } else if (digits.length === 11 && digits.startsWith("1")) {
    cc = "1";
    national = digits.slice(1);
  } else if (digits.length === 10) {
    cc = "1";
    national = digits;
  } else {
    // Unknown format — return input unchanged
    return raw;
  }

  // Format NANP national numbers as AAA-BBB-CCCC
  let nationalFormatted: string;
  if (national.length === 10) {
    nationalFormatted = `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`;
  } else if (national.length === 7) {
    nationalFormatted = `${national.slice(0, 3)}-${national.slice(3)}`;
  } else {
    // Chunk into 3-3-... groups
    const groups: string[] = [];
    let i = 0;
    while (i < national.length) {
      const size = national.length - i >= 4 ? 3 : national.length - i;
      groups.push(national.slice(i, i + size));
      i += size;
    }
    nationalFormatted = groups.join("-");
  }

  return `+${cc} ${nationalFormatted}`;
}